import axios from 'axios';
import { io } from 'socket.io-client';
import { logger } from '@/lib/logger';
// Detect device type (for LAN testing on real devices)
const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

// Detect if running inside Docker (frontend container)
const isDocker =
    typeof window !== "undefined" &&
    (window.location.hostname.includes("frontend_container") ||
        window.location.hostname.includes("docker") ||
        window.location.hostname.includes("container"));

// Pick correct base depending on env + device + docker
function pickBase(dev, lan, prod, docker) {
    const isHostedBrowser =
        typeof window !== "undefined" &&
        (
            window.location.hostname.endsWith(".onrender.com") ||
            window.location.hostname === "qitplaylist.com" ||
            window.location.hostname.endsWith(".qitplaylist.com")
        );

    const normalizeProdBase = (value) => {
        const candidate = String(value || "").trim();
        if (!candidate) return "/api";
        if (candidate.startsWith("/")) return candidate;

        const fallbackToRelativeApi = (parsed) => {
            const path = String(parsed?.pathname || "").replace(/\/+$/, "");
            if (path && path !== "/") {
                return `${path}${parsed?.search || ""}${parsed?.hash || ""}`;
            }
            return "/api";
        };

        let parsedUrl = null;
        try {
            if (typeof window !== "undefined") {
                parsedUrl = new URL(candidate, window.location.origin);
            }
        } catch {
            return fallbackToRelativeApi();
        }

        // Prevent mixed-content in production if an http:// base sneaks into env vars.
        if (
            typeof window !== "undefined" &&
            window.location.protocol === "https:" &&
            candidate.startsWith("http://")
        ) {
            return fallbackToRelativeApi(parsedUrl);
        }
        // Prevent stale explicit :8080 browser targets in production.
        if (
            typeof window !== "undefined" &&
            window.location.hostname !== "localhost" &&
            window.location.hostname !== "127.0.0.1" &&
            /:8080(?:\/|$)/.test(candidate)
        ) {
            return fallbackToRelativeApi(parsedUrl);
        }
        // Keep frontend domain interchangeable (Render/custom domain) by forcing same-origin proxy paths.
        if (
            typeof window !== "undefined" &&
            parsedUrl &&
            parsedUrl.origin !== window.location.origin
        ) {
            return fallbackToRelativeApi(parsedUrl);
        }
        return candidate;
    };

    // ✅ Always use /api/* in the browser so Nginx proxies requests
    if (isHostedBrowser) return normalizeProdBase(prod || dev || "/api");
    if (import.meta.env.PROD) return normalizeProdBase(prod);
    if (isDocker && docker) return "/api"; // override: browser cannot resolve container names
    return isMobile ? (lan || dev) : dev;
}

const trimTrailingSlashes = (value) => String(value || "").replace(/\/+$/, "");
const ensureAuthBase = (value) => {
    const normalized = trimTrailingSlashes(value || "/api");
    return normalized.endsWith("/auth") ? normalized : `${normalized}/auth`;
};
const AUTH_BASE = ensureAuthBase(
    pickBase(
        import.meta.env.VITE_AUTH_API_BASE,
        import.meta.env.VITE_AUTH_API_BASE_LAN,
        import.meta.env.VITE_AUTH_API_BASE_PROD,
        import.meta.env.VITE_AUTH_API_BASE_DOCKER
    )
);

const normalizeSongsApiBase = (value) => {
    const normalized = trimTrailingSlashes(value || "/api");
    if (normalized.endsWith("/v1/songs/playlists")) {
        const stripped = normalized.slice(0, -"/v1/songs/playlists".length);
        return stripped || "/api";
    }
    return normalized;
};

const SONGS_BASE = normalizeSongsApiBase(
    pickBase(
        import.meta.env.VITE_SONGS_API_BASE,
        import.meta.env.VITE_SONGS_API_BASE_LAN,
        import.meta.env.VITE_SONGS_API_BASE_PROD,
        import.meta.env.VITE_SONGS_API_BASE_DOCKER
    )
);

// Votes have a service prefix even when deployment environment variables are absent.
const rawVotesBase = pickBase(
    import.meta.env.VITE_VOTES_API_BASE,
    import.meta.env.VITE_VOTES_API_BASE_LAN,
    import.meta.env.VITE_VOTES_API_BASE_PROD,
    import.meta.env.VITE_VOTES_API_BASE_DOCKER
);
const VOTES_BASE = !rawVotesBase || trimTrailingSlashes(rawVotesBase) === "/api"
    ? "/api/v1/votes" : trimTrailingSlashes(rawVotesBase);

const sessionBase = "/api/session";



// -----------------------------------
// Axios instance with default configuration
// -----------------------------------
const axiosInstance = axios.create({
    baseURL: "/",
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
    maxRedirects: 5,
});

axiosInstance.defaults.withCredentials = true;

// Request interceptor to add auth token
axiosInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        const skipAuth = Boolean(config?.skipAuth || config?.headers?.["X-Skip-Auth"]);
        config.headers = config.headers || {};
        const hasAuthorizationHeader = typeof config?.headers?.Authorization !== "undefined";
        config.headers["X-Correlation-ID"] = logger.correlationId();
        if (token && !skipAuth && !hasAuthorizationHeader) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
// Response interceptor for error handling
axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        // Only follow redirects when the target stays same-origin.
        // Cross-origin follow-ups from XHR cause CORS failures (e.g. payment-service redirects).
        if ([307, 308].includes(error.response?.status)) {
            const redirectUrl = error.response?.headers?.location;
            if (redirectUrl && typeof window !== "undefined") {
                try {
                    const target = new URL(redirectUrl, window.location.origin);
                    if (target.origin === window.location.origin) {
                        const nextUrl = `${target.pathname}${target.search}${target.hash}`;
                        return axiosInstance.request({
                            ...error.config,
                            url: nextUrl,
                        });
                    }
                    logger.warn("api.redirect.cross_origin_blocked", { origin: target.origin });
                } catch {
                    // Ignore invalid redirect targets and let original error bubble.
                }
            }
        }

        // Let your UserContext handle token refresh
        if (error.response?.status !== 429) {
            logger.warn("api.request.failed", {
                status: error.response?.status,
                method: error.config?.method,
                path: String(error.config?.url || "").split("?")[0],
            });
        }
        return Promise.reject(error);
    }
);

// -----------------------------------
// Centralized API endpoints (per service)
// -----------------------------------
export const API_ENDPOINTS = {
    // Auth Service
    auth: {
        base: AUTH_BASE,
        login: `${AUTH_BASE}/v1/login`,
        register: `${AUTH_BASE}/v1/register`,
        refresh: `${AUTH_BASE}/v1/refresh`,
        guestLogin: `${AUTH_BASE}/v1/guest-login`,
        stats: `${AUTH_BASE}/v1/stats`,
        users: `${AUTH_BASE}/v1/users`,
        userInvestigation: (userId) => `${AUTH_BASE}/v1/admin/users/${encodeURIComponent(userId)}/investigation`,
        userInvestigationNotes: (userId) => `${AUTH_BASE}/v1/admin/users/${encodeURIComponent(userId)}/notes`,
        userInvestigationReports: (userId) => `${AUTH_BASE}/v1/admin/users/${encodeURIComponent(userId)}/reports`,
        moderationReport: (reportId) => `${AUTH_BASE}/v1/admin/reports/${encodeURIComponent(reportId)}`,
        resolveUserLabels: `${AUTH_BASE}/v1/admin/resolve-user-labels`,
        resolveCurrentUserLabels: `${AUTH_BASE}/v1/resolve-user-labels`,
        systemHealth: `${AUTH_BASE}/v1/admin/system-health`,
        adminAuditLog: `${AUTH_BASE}/v1/admin/audit-log`,
        platformSettings: `${AUTH_BASE}/v1/platform-settings`,
        brandingAssets: `${AUTH_BASE}/v1/platform-settings/branding-assets`,
        qrBrandImage: `${AUTH_BASE}/v1/platform-settings/qr-logo`,
        currentUser: `${AUTH_BASE}/v1/me`,
        updateAvatar: `${AUTH_BASE}/v1/me/avatar`,
        updateUsername: `${AUTH_BASE}/v1/me/username`,
        health: `${pickBase(
            import.meta.env.VITE_AUTH_API_BASE,
            import.meta.env.VITE_AUTH_API_BASE_LAN,
            import.meta.env.VITE_AUTH_API_BASE_PROD,
            import.meta.env.VITE_AUTH_API_BASE_DOCKER
        )}/health`,
        recentActivities: `${AUTH_BASE}/v1/me/recent-activities`,
        announcements: {
            base: `${AUTH_BASE}/v1/announcements`,
            upload: `${AUTH_BASE}/v1/announcements/upload`,
            pending: `${AUTH_BASE}/v1/announcements/my/pending`,
            markSeen: `${AUTH_BASE}/v1/announcements/my/seen`,
        },
        accountNotices: {
            base: `${AUTH_BASE}/v1/account-notices`,
            mine: `${AUTH_BASE}/v1/account-notices/my`,
        },
    },



    // Playlists
    playlists: {
        base: pickBase(
            import.meta.env.VITE_PLAYLISTS_API_BASE,
            import.meta.env.VITE_PLAYLISTS_API_BASE_LAN,
            import.meta.env.VITE_PLAYLISTS_API_BASE_PROD,
            import.meta.env.VITE_PLAYLISTS_API_BASE_DOCKER
        ),
        resolveAnalyticsLabels: `${pickBase(
            import.meta.env.VITE_PLAYLISTS_API_BASE,
            import.meta.env.VITE_PLAYLISTS_API_BASE_LAN,
            import.meta.env.VITE_PLAYLISTS_API_BASE_PROD,
            import.meta.env.VITE_PLAYLISTS_API_BASE_DOCKER
        )}/v1/playlists/admin/resolve-analytics-labels`,
        list: `${pickBase(
            import.meta.env.VITE_PLAYLISTS_API_BASE,
            import.meta.env.VITE_PLAYLISTS_API_BASE_LAN,
            import.meta.env.VITE_PLAYLISTS_API_BASE_PROD,
            import.meta.env.VITE_PLAYLISTS_API_BASE_DOCKER
        )}/v1/playlists`,
        create: `${pickBase(
            import.meta.env.VITE_PLAYLISTS_API_BASE,
            import.meta.env.VITE_PLAYLISTS_API_BASE_LAN,
            import.meta.env.VITE_PLAYLISTS_API_BASE_PROD,
            import.meta.env.VITE_PLAYLISTS_API_BASE_DOCKER
        )}/v1/playlists`,

        // ✅ Public playlists
        publicList: `${pickBase(
            import.meta.env.VITE_PLAYLISTS_API_BASE,
            import.meta.env.VITE_PLAYLISTS_API_BASE_LAN,
            import.meta.env.VITE_PLAYLISTS_API_BASE_PROD,
            import.meta.env.VITE_PLAYLISTS_API_BASE_DOCKER
        )}/v1/playlists/public`,

        publicByCategory: (category) =>
            `${pickBase(
                import.meta.env.VITE_PLAYLISTS_API_BASE,
                import.meta.env.VITE_PLAYLISTS_API_BASE_LAN,
                import.meta.env.VITE_PLAYLISTS_API_BASE_PROD,
                import.meta.env.VITE_PLAYLISTS_API_BASE_DOCKER
            )}/v1/playlists/public/category/${category}`,

        publicByVenue: (venueId) =>
            `${pickBase(
                import.meta.env.VITE_PLAYLISTS_API_BASE,
                import.meta.env.VITE_PLAYLISTS_API_BASE_LAN,
                import.meta.env.VITE_PLAYLISTS_API_BASE_PROD,
                import.meta.env.VITE_PLAYLISTS_API_BASE_DOCKER
            )}/v1/playlists/public/venue/${venueId}`,

        getOne: (id) => `${pickBase(
            import.meta.env.VITE_PLAYLISTS_API_BASE,
            import.meta.env.VITE_PLAYLISTS_API_BASE_LAN,
            import.meta.env.VITE_PLAYLISTS_API_BASE_PROD,
            import.meta.env.VITE_PLAYLISTS_API_BASE_DOCKER
        )}/v1/playlists/${id}`,

        update: (id) => `${pickBase(
            import.meta.env.VITE_PLAYLISTS_API_BASE,
            import.meta.env.VITE_PLAYLISTS_API_BASE_LAN,
            import.meta.env.VITE_PLAYLISTS_API_BASE_PROD,
            import.meta.env.VITE_PLAYLISTS_API_BASE_DOCKER
        )}/v1/playlists/${id}`,

        remove: (id) => `${pickBase(
            import.meta.env.VITE_PLAYLISTS_API_BASE,
            import.meta.env.VITE_PLAYLISTS_API_BASE_LAN,
            import.meta.env.VITE_PLAYLISTS_API_BASE_PROD,
            import.meta.env.VITE_PLAYLISTS_API_BASE_DOCKER
        )}/v1/playlists/${id}`,

        session: (sessionId) =>
            `${pickBase(
                import.meta.env.VITE_PLAYLISTS_API_BASE,
                import.meta.env.VITE_PLAYLISTS_API_BASE_LAN,
                import.meta.env.VITE_PLAYLISTS_API_BASE_PROD,
                import.meta.env.VITE_PLAYLISTS_API_BASE_DOCKER
            )}/v1/playlists/session/${sessionId}`,

        qitPlaylistAdmin: `${pickBase(
            import.meta.env.VITE_PLAYLISTS_API_BASE,
            import.meta.env.VITE_PLAYLISTS_API_BASE_LAN,
            import.meta.env.VITE_PLAYLISTS_API_BASE_PROD,
            import.meta.env.VITE_PLAYLISTS_API_BASE_DOCKER
        )}/v1/playlists/qit-playlist/admin`,
        qitTrendingSongPopularity: `${pickBase(
            import.meta.env.VITE_PLAYLISTS_API_BASE,
            import.meta.env.VITE_PLAYLISTS_API_BASE_LAN,
            import.meta.env.VITE_PLAYLISTS_API_BASE_PROD,
            import.meta.env.VITE_PLAYLISTS_API_BASE_DOCKER
        )}/v1/playlists/qit-trending/admin/song-popularity`,
        qitTrendingAdminSong: (songId) => `${pickBase(
            import.meta.env.VITE_PLAYLISTS_API_BASE,
            import.meta.env.VITE_PLAYLISTS_API_BASE_LAN,
            import.meta.env.VITE_PLAYLISTS_API_BASE_PROD,
            import.meta.env.VITE_PLAYLISTS_API_BASE_DOCKER
        )}/v1/playlists/qit-trending/admin/songs/${encodeURIComponent(songId)}`,
    },

    songs: {
        base: SONGS_BASE,
        add: (playlistId) =>
            `${SONGS_BASE}/v1/songs/playlists/${playlistId}/songs`,
        getOne: (playlistId, songId) =>
            `${SONGS_BASE}/v1/songs/playlists/${playlistId}/songs/${songId}`,
        update: (playlistId, songId) =>
            `${SONGS_BASE}/v1/songs/playlists/${playlistId}/songs/${songId}`,
        reorder: (playlistId) =>
            `${SONGS_BASE}/v1/songs/playlists/${playlistId}/songs/reorder`,
        remove: (playlistId, songId) =>
            `${SONGS_BASE}/v1/songs/playlists/${playlistId}/songs/${songId}`,
    },

    votes: {
        base: VOTES_BASE,

        // POST → cast vote
        cast: `${VOTES_BASE}`,

        // GET → balance
        balance: `${VOTES_BASE}/balance`,

        // POST → recharge
        recharge: `${VOTES_BASE}/recharge`,

        grantPolicy: `${VOTES_BASE}/admin/grant-policy`,

        analytics: `${VOTES_BASE}/admin/analytics`,

        leaderboard: `${VOTES_BASE}/leaderboard`,

        audit: `${VOTES_BASE}/admin/audit`,
        userAudit: (userId) => `${VOTES_BASE}/admin/users/${encodeURIComponent(userId)}/audit`,

        // POST → transfer votes between users
        transfer: `${VOTES_BASE}/transfer`,

        // GET → history
        history: `${VOTES_BASE}/history`,

        // GET → transactions
        transactions: `${VOTES_BASE}/transactions`,

        earningsSummary: `${VOTES_BASE}/earnings/summary`,

        earningsWithdrawals: `${VOTES_BASE}/earnings/withdrawals`,

        earningsSettings: `${VOTES_BASE}/earnings/settings`,

        earningsReset: `${VOTES_BASE}/earnings/reset`,

        verifyStripePayoutAccount: (accountId) =>
            `${VOTES_BASE}/payout/stripe/verify-account/${encodeURIComponent(accountId)}`,

        stripeConnectStatus: `${VOTES_BASE}/payout/stripe/connect/status`,

        stripeConnectOnboarding: `${VOTES_BASE}/payout/stripe/connect/onboarding`,


        // GET → transaction by id
        transactionById: (id) =>
            `${VOTES_BASE}/transactions/${id}`,

        // GET → total votes by playlist
        totalByPlaylist: (playlistId) =>
            `${VOTES_BASE}/playlists/${playlistId}/total`,
    },

    // Venues Service
    venues: {
        base: pickBase(
            import.meta.env.VITE_VENUES_API_BASE,
            import.meta.env.VITE_VENUES_API_BASE_LAN,
            import.meta.env.VITE_VENUES_API_BASE_PROD,
            import.meta.env.VITE_VENUES_API_BASE_DOCKER
        ),
        list: `${pickBase(
            import.meta.env.VITE_VENUES_API_BASE,
            import.meta.env.VITE_VENUES_API_BASE_LAN,
            import.meta.env.VITE_VENUES_API_BASE_PROD,
            import.meta.env.VITE_VENUES_API_BASE_DOCKER
        )}/v1/venues`,
        create: `${pickBase(
            import.meta.env.VITE_VENUES_API_BASE,
            import.meta.env.VITE_VENUES_API_BASE_LAN,
            import.meta.env.VITE_VENUES_API_BASE_PROD,
            import.meta.env.VITE_VENUES_API_BASE_DOCKER
        )}/v1/venues`,
        getOne: (venueId) => `${pickBase(
            import.meta.env.VITE_VENUES_API_BASE,
            import.meta.env.VITE_VENUES_API_BASE_LAN,
            import.meta.env.VITE_VENUES_API_BASE_PROD,
            import.meta.env.VITE_VENUES_API_BASE_DOCKER
        )}/v1/venues/${venueId}`,
        update: (venueId) => `${pickBase(
            import.meta.env.VITE_VENUES_API_BASE,
            import.meta.env.VITE_VENUES_API_BASE_LAN,
            import.meta.env.VITE_VENUES_API_BASE_PROD,
            import.meta.env.VITE_VENUES_API_BASE_DOCKER
        )}/v1/venues/${venueId}`,
        remove: (venueId) => `${pickBase(
            import.meta.env.VITE_VENUES_API_BASE,
            import.meta.env.VITE_VENUES_API_BASE_LAN,
            import.meta.env.VITE_VENUES_API_BASE_PROD,
            import.meta.env.VITE_VENUES_API_BASE_DOCKER
        )}/v1/venues/${venueId}`,
    },

    // Search
    search: {
        resolve: '/api/v1/search/resolve',
        resolvePlaylist: '/api/v1/search/resolve-playlist',
        youtube: '/api/v1/search/youtube',
        spotify: '/api/v1/search/spotify',
        soundcloud: '/api/v1/search/soundcloud',
        applemusic: '/api/v1/search/applemusic',
        netflix: '/api/v1/search/netflix',
        // Backward-compatible alias used in a few older call sites.
        apple: '/api/v1/search/applemusic',
    },



    // Session endpoints
    session: {
        base: pickBase(
            import.meta.env.VITE_SESSION_API_BASE,
            import.meta.env.VITE_SESSION_API_BASE_LAN,
            import.meta.env.VITE_SESSION_API_BASE_PROD,
            import.meta.env.VITE_SESSION_API_BASE_DOCKER
        ),
        adminAnalytics: `${pickBase(
            import.meta.env.VITE_SESSION_API_BASE,
            import.meta.env.VITE_SESSION_API_BASE_LAN,
            import.meta.env.VITE_SESSION_API_BASE_PROD,
            import.meta.env.VITE_SESSION_API_BASE_DOCKER
        )}/v1/sessions/admin/analytics`,
        resolveActivityLabels: `${pickBase(
            import.meta.env.VITE_SESSION_API_BASE,
            import.meta.env.VITE_SESSION_API_BASE_LAN,
            import.meta.env.VITE_SESSION_API_BASE_PROD,
            import.meta.env.VITE_SESSION_API_BASE_DOCKER
        )}/v1/sessions/admin/resolve-activity-labels`,
        adminUserHistory: (userId) => `${pickBase(
            import.meta.env.VITE_SESSION_API_BASE,
            import.meta.env.VITE_SESSION_API_BASE_LAN,
            import.meta.env.VITE_SESSION_API_BASE_PROD,
            import.meta.env.VITE_SESSION_API_BASE_DOCKER
        )}/v1/sessions/admin/users/${encodeURIComponent(userId)}/sessions`,
        create: `${pickBase(
            import.meta.env.VITE_SESSION_API_BASE,
            import.meta.env.VITE_SESSION_API_BASE_LAN,
            import.meta.env.VITE_SESSION_API_BASE_PROD,
            import.meta.env.VITE_SESSION_API_BASE_DOCKER
        )}/v1/sessions/`,
        get: (sessionId) => `${pickBase(
            import.meta.env.VITE_SESSION_API_BASE,
            import.meta.env.VITE_SESSION_API_BASE_LAN,
            import.meta.env.VITE_SESSION_API_BASE_PROD,
            import.meta.env.VITE_SESSION_API_BASE_DOCKER
        )}/v1/sessions/${encodeURIComponent(sessionId)}`,
        // 🔴 FIX THIS LINE - Remove the sessionBase variable, use the proper path
        join: (sessionId) => `${pickBase(
            import.meta.env.VITE_SESSION_API_BASE,
            import.meta.env.VITE_SESSION_API_BASE_LAN,
            import.meta.env.VITE_SESSION_API_BASE_PROD,
            import.meta.env.VITE_SESSION_API_BASE_DOCKER
        )}/v1/sessions/${sessionId}/join`,

        // Also fix these if they use the same pattern:
        leave: (sessionId) => `${pickBase(
            import.meta.env.VITE_SESSION_API_BASE,
            import.meta.env.VITE_SESSION_API_BASE_LAN,
            import.meta.env.VITE_SESSION_API_BASE_PROD,
            import.meta.env.VITE_SESSION_API_BASE_DOCKER
        )}/v1/sessions/${sessionId}/leave`,

        // Canonical host end-session route (hard delete)
        delete: (sessionId) => `${pickBase(
            import.meta.env.VITE_SESSION_API_BASE,
            import.meta.env.VITE_SESSION_API_BASE_LAN,
            import.meta.env.VITE_SESSION_API_BASE_PROD,
            import.meta.env.VITE_SESSION_API_BASE_DOCKER
        )}/v1/sessions/${sessionId}`,

        // Backward-compatible alias used by older session-service helpers.
        end: (sessionId) => `${pickBase(
            import.meta.env.VITE_SESSION_API_BASE,
            import.meta.env.VITE_SESSION_API_BASE_LAN,
            import.meta.env.VITE_SESSION_API_BASE_PROD,
            import.meta.env.VITE_SESSION_API_BASE_DOCKER
        )}/v1/sessions/${sessionId}`,

        // Legacy endpoint kept as fallback for older deployments.
        endLegacy: (sessionId) => `${pickBase(
            import.meta.env.VITE_SESSION_API_BASE,
            import.meta.env.VITE_SESSION_API_BASE_LAN,
            import.meta.env.VITE_SESSION_API_BASE_PROD,
            import.meta.env.VITE_SESSION_API_BASE_DOCKER
        )}/v1/sessions/end/${sessionId}`,
    },


    payments: {
        base: pickBase(
            import.meta.env.VITE_PAYMENTS_API_BASE,
            import.meta.env.VITE_PAYMENTS_API_BASE_LAN,
            import.meta.env.VITE_PAYMENTS_API_BASE_PROD,
            import.meta.env.VITE_PAYMENTS_API_BASE_DOCKER
        ),

        adminRevenueReport: `${pickBase(
            import.meta.env.VITE_PAYMENTS_API_BASE,
            import.meta.env.VITE_PAYMENTS_API_BASE_LAN,
            import.meta.env.VITE_PAYMENTS_API_BASE_PROD,
            import.meta.env.VITE_PAYMENTS_API_BASE_DOCKER
        )}/v1/payments/admin/revenue-report`,
        reportSchedules: `${pickBase(
            import.meta.env.VITE_PAYMENTS_API_BASE,
            import.meta.env.VITE_PAYMENTS_API_BASE_LAN,
            import.meta.env.VITE_PAYMENTS_API_BASE_PROD,
            import.meta.env.VITE_PAYMENTS_API_BASE_DOCKER
        )}/v1/payments/admin/report-schedules`,
        reportSchedule: (id) => `${pickBase(
            import.meta.env.VITE_PAYMENTS_API_BASE,
            import.meta.env.VITE_PAYMENTS_API_BASE_LAN,
            import.meta.env.VITE_PAYMENTS_API_BASE_PROD,
            import.meta.env.VITE_PAYMENTS_API_BASE_DOCKER
        )}/v1/payments/admin/report-schedules/${id}`,
        reportHistory: `${pickBase(
            import.meta.env.VITE_PAYMENTS_API_BASE,
            import.meta.env.VITE_PAYMENTS_API_BASE_LAN,
            import.meta.env.VITE_PAYMENTS_API_BASE_PROD,
            import.meta.env.VITE_PAYMENTS_API_BASE_DOCKER
        )}/v1/payments/admin/report-history`,
        reportDownload: (id) => `${pickBase(
            import.meta.env.VITE_PAYMENTS_API_BASE,
            import.meta.env.VITE_PAYMENTS_API_BASE_LAN,
            import.meta.env.VITE_PAYMENTS_API_BASE_PROD,
            import.meta.env.VITE_PAYMENTS_API_BASE_DOCKER
        )}/v1/payments/admin/report-history/${id}/download`,

        initiate: `${pickBase(
            import.meta.env.VITE_PAYMENTS_API_BASE,
            import.meta.env.VITE_PAYMENTS_API_BASE_LAN,
            import.meta.env.VITE_PAYMENTS_API_BASE_PROD,
            import.meta.env.VITE_PAYMENTS_API_BASE_DOCKER
        )}/v1/payments/initiate`,

        initiateCustom: `${pickBase(
            import.meta.env.VITE_PAYMENTS_API_BASE,
            import.meta.env.VITE_PAYMENTS_API_BASE_LAN,
            import.meta.env.VITE_PAYMENTS_API_BASE_PROD,
            import.meta.env.VITE_PAYMENTS_API_BASE_DOCKER
        )}/v1/payments/initiate-custom`,

        capturePaypal: (paymentId) =>
            `${pickBase(
                import.meta.env.VITE_PAYMENTS_API_BASE,
                import.meta.env.VITE_PAYMENTS_API_BASE_LAN,
                import.meta.env.VITE_PAYMENTS_API_BASE_PROD,
                import.meta.env.VITE_PAYMENTS_API_BASE_DOCKER
            )}/v1/payments/paypal/capture/${paymentId}`,

        getOne: (id) =>
            `${pickBase(
                import.meta.env.VITE_PAYMENTS_API_BASE,
                import.meta.env.VITE_PAYMENTS_API_BASE_LAN,
                import.meta.env.VITE_PAYMENTS_API_BASE_PROD,
                import.meta.env.VITE_PAYMENTS_API_BASE_DOCKER
            )}/v1/payments/${id}`,

        status: (id) =>
            `${pickBase(
                import.meta.env.VITE_PAYMENTS_API_BASE,
                import.meta.env.VITE_PAYMENTS_API_BASE_LAN,
                import.meta.env.VITE_PAYMENTS_API_BASE_PROD,
                import.meta.env.VITE_PAYMENTS_API_BASE_DOCKER
            )}/v1/payments/${id}/status`,

        userHistory: (userId) =>
            `${pickBase(
                import.meta.env.VITE_PAYMENTS_API_BASE,
                import.meta.env.VITE_PAYMENTS_API_BASE_LAN,
                import.meta.env.VITE_PAYMENTS_API_BASE_PROD,
                import.meta.env.VITE_PAYMENTS_API_BASE_DOCKER
            )}/v1/users/${userId}/payments`,

        adminUserHistory: (userId) =>
            `${pickBase(
                import.meta.env.VITE_PAYMENTS_API_BASE,
                import.meta.env.VITE_PAYMENTS_API_BASE_LAN,
                import.meta.env.VITE_PAYMENTS_API_BASE_PROD,
                import.meta.env.VITE_PAYMENTS_API_BASE_DOCKER
            )}/v1/payments/admin/users/${encodeURIComponent(userId)}`,

        plans: `${pickBase(
            import.meta.env.VITE_PAYMENTS_API_BASE,
            import.meta.env.VITE_PAYMENTS_API_BASE_LAN,
            import.meta.env.VITE_PAYMENTS_API_BASE_PROD,
            import.meta.env.VITE_PAYMENTS_API_BASE_DOCKER
        )}/v1/plans/`,

        planById: (planId) =>
            `${pickBase(
                import.meta.env.VITE_PAYMENTS_API_BASE,
                import.meta.env.VITE_PAYMENTS_API_BASE_LAN,
                import.meta.env.VITE_PAYMENTS_API_BASE_PROD,
                import.meta.env.VITE_PAYMENTS_API_BASE_DOCKER
            )}/v1/plans/${planId}`,
    }
};



// -----------------------------------
// ✅ Backward compatibility exports
// -----------------------------------
export const API_BASE = API_ENDPOINTS.auth.base;





export const LEGACY_ENDPOINTS = {
    PLAYLISTS: API_ENDPOINTS.playlists.create,
    SEARCH_SONGS: `${API_ENDPOINTS.playlists.base}/v1/songs/search`,
    UPLOAD_IMAGE: `${API_ENDPOINTS.playlists.base}/v1/upload/image`,
    HEALTH: `${API_ENDPOINTS.playlists.base}/health`,
    VENUES: API_ENDPOINTS.venues.create,        
    VENUES_LIST: API_ENDPOINTS.venues.list,     
    UPLOAD: `${API_ENDPOINTS.playlists.base}/v1/playlists/upload`,
};

// Export the axios instance as default
export default axiosInstance;
